import camelcaseKeys from 'camelcase-keys';
import { decamelize } from 'fast-case';
import ky, {
  type Input,
  type Options,
  type ResponsePromise,
  type SearchParamsOption,
} from 'ky';
import snakecaseKeys from 'snakecase-keys';
// @ts-expect-error - urlcat is not typed
import urlcat from 'urlcat';
import { z, ZodError, type ZodSchema, type ZodTuple, type ZodType } from 'zod';

import { fnv1a, type Json } from '@vestigas/utils';

import {
  buildResponseValidationReport,
  type ResponseValidationProps,
} from './safeParseResponse';
import {
  isSessionExpiredError,
  SessionExpiredError,
} from './SessionExpiredError';
import { VestigasHTTPError } from './VestigasHTTPError';

export { HTTPError } from 'ky';
export { VestigasHTTPError } from './VestigasHTTPError';
export type {
  ErrorResponseBody,
  ValidationErrorDetail,
} from './VestigasHTTPError';

const trailingSlashRegex = /\/+$/;
const startsWithUuidRegex = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}/i;
// Keys starting with `$` are OData system query options ($top/$filter/$skip/...).
// Case-converting them strips the `$` (e.g. `$top` -> `top`) and breaks the request. INT2-1262.
const startsWithDollarRegex = /^\$/;
// Path segments that identify a record rather than the endpoint, collapsed so
// one endpoint stays one Sentry group.
const digitsOnlySegmentRegex = /^\d+$/;
const ID_PLACEHOLDER = '{id}';
const INVALID_CONTENT_TYPE_CODE = 'invalid_content_type';

const CORS_WHITELISTED_EXTRA_HEADERS = [
  'Admin-Portal-Version-Git',
  'Admin-Portal-Version-Semantic',
  'Customer-Portal-Version-Git',
  'Customer-Portal-Version-Semantic',
] as const;

/**
 * Convert a string to snake_case.
 * Does not convert UUIDs, because that would break them.
 */
const toSnakeCase = (variableName: string) =>
  typeof variableName === 'string' && !startsWithUuidRegex.test(variableName)
    ? decamelize(variableName)
    : variableName;

export type KyValidationOptions = {
  body?: ZodSchema;
  /**
   * Parse the response without `reportInput`, so logged issues carry paths,
   * codes and messages only — no values at all.
   *
   * NOT the sanitization switch: redaction, masking, scrubbing, shape
   * summaries and caps always run on logged issues. This omits the values at
   * the source on top of that, for endpoints whose whole body is delicate.
   */
  isResponseSensitive?: boolean;
  pathSegments?: ZodTuple;
  /**
   * Validate the response body **of a 2xx response**. On success the parsed
   * data replaces the body (preserving zod key-stripping / `.default()` /
   * `.catch()`); on failure the raw body passes through untouched and the
   * drift is logged. Never throws.
   *
   * Error responses are never validated: they carry `{detail}`, not the
   * success shape, so every one would fail and consume the logger's
   * per-endpoint daily cap that real drift needs. Only validation is gated, so
   * on a converter instance an error body is still camelCased, and on a
   * `withCaseConverter: false` one (`vestigasApiNoConversion`) it is returned
   * exactly as it arrived — with validation gated there is nothing left for
   * the tap to do. A *success* body is not comparable: a validator that
   * passes hands back zod's own object, so the response is rebuilt from
   * `JSON.stringify` and every key the schema does not declare is stripped.
   *
   * Requires a schema whose output survives a JSON round trip, because the
   * substituted body is re-serialized. `z.coerce.date()` is the loud case
   * (`Date` → string). The quiet one is `undefined`: `JSON.stringify` DROPS a
   * key whose value is `undefined` and turns `undefined` inside an array into
   * `null`. Plain `.optional()` is safe — zod leaves an absent key absent — but
   * `.catch(undefined)` and `.transform(() => undefined)` both leave an own key
   * holding `undefined`, which then disappears, changing `'field' in body` and
   * `Object.keys(body).length`. The `ZodType<Json>` bound rejects most
   * non-round-trippable outputs at compile time, the `.transform()` one
   * included, but not `.catch(undefined)` on an optional field — that infers an
   * *optional* property, which the `Json` index signature accepts — so of the
   * two identical hazards only one is caught and the other is on the author.
   * Use the `safeParseResponse` escape hatch when that matters.
   *
   * Drift is only *reported* if the instance was built with a `logger`.
   * `vestigasPublicApi` is not — a validator declared on a public route
   * validates and substitutes, but reports nothing.
   */
  response?: ZodType<Json>;
  searchParams?: ZodSchema;
};

export type KyRequestOptions = Options & { validate?: KyValidationOptions };

export type KyHookOptions = KyRequestOptions & {
  withCaseConverter?: boolean;
  useIdToken?: boolean;
};

export type AuthService = {
  getAccessToken: () => string | undefined;
  getIdToken: () => string | undefined;
  refreshTokens: () => Promise<string | undefined>;
  wasLastRefreshSessionExpiry: () => boolean;
};

export type Logger = Partial<{
  error: (options: any) => void;
  responseValidation: (props: ResponseValidationProps) => void;
}>;

// Extend the ky instance type to include our custom options
export type VestigasKyInstance = Omit<typeof ky, 'create'> & {
  <T = unknown>(input: Input | URL, options?: KyRequestOptions): Promise<T>;
  get: <T = unknown>(
    input: Input | URL,
    options?: KyRequestOptions,
  ) => ResponsePromise<T>;
  post: <T = unknown>(
    input: Input | URL,
    options?: KyRequestOptions,
  ) => ResponsePromise<T>;
  put: <T = unknown>(
    input: Input | URL,
    options?: KyRequestOptions,
  ) => ResponsePromise<T>;
  patch: <T = unknown>(
    input: Input | URL,
    options?: KyRequestOptions,
  ) => ResponsePromise<T>;
  head: <T = unknown>(
    input: Input | URL,
    options?: KyRequestOptions,
  ) => ResponsePromise<T>;
  delete: <T = unknown>(
    input: Input | URL,
    options?: KyRequestOptions,
  ) => ResponsePromise<T>;
};

/**
 * Converts the keys of an object to camelCase. Keeps object keys that start with a UUID as they are, because the conversion would break the UUID.
 * @param data - The object to convert.
 * @returns The converted object with camelCase keys.
 */
export const camelcaseKeysFromApi = (data: any) =>
  camelcaseKeys(data, {
    deep: true,
    exclude: [startsWithUuidRegex, startsWithDollarRegex], // exclude custom fields (UUID keys) and OData `$` params from conversion
  });

/**
 * Converts the keys of an object to snake_case. Keeps object keys that start with a UUID as they are, because the conversion would break the UUID.
 * @param data - The object to convert.
 * @returns The converted object with snake_case keys.
 */
export const snakecaseKeysForApi = (data: any) =>
  snakecaseKeys(data, {
    deep: true,
    exclude: [startsWithUuidRegex, startsWithDollarRegex], // exclude custom fields (UUID keys) and OData `$` params from conversion
  });

/**
 * Processes a request body and validates it using a Zod schema, if provided.
 * @param body - The request body to process.
 * @param validation - The Zod schema to validate the request body against.
 * @param withCaseConverter - Whether to convert all request body keys to snake_case.
 * @returns The processed request body.
 */
export function processRequestBody(
  body: BodyInit | undefined,
  validation?: ZodSchema,
  withCaseConverter = true,
): BodyInit | undefined {
  if (!body) {
    return undefined;
  }

  // FIXME: We need a special handler for FormData for zod, the normal zod parsing doesn't work with FormData.
  if (body instanceof FormData) {
    const snakeCasedBody = new FormData();

    for (const [key, value] of body.entries()) {
      snakeCasedBody.append(toSnakeCase(key), value);
    }

    return snakeCasedBody;
  }

  // Special case when body is a Blob, for example when uploading a file.
  if (body instanceof Blob) {
    return body;
  }

  let jsonBody: unknown;

  if (typeof body === 'string') {
    try {
      jsonBody = JSON.parse(body);
    } catch {
      return body;
    }
  } else {
    jsonBody = body;
  }

  if (validation) {
    // Validate and sanitize the body. Also strip all unknown keys. Throw a ZodError if validation fails.
    jsonBody = validation.parse(jsonBody);
  }

  if (!withCaseConverter) {
    return JSON.stringify(jsonBody);
  }

  // Skip conversion for arrays of strings/numbers
  if (
    Array.isArray(jsonBody) &&
    jsonBody.every(
      (item) => typeof item === 'string' || typeof item === 'number',
    )
  ) {
    return JSON.stringify(jsonBody);
  }

  return JSON.stringify(snakecaseKeysForApi(jsonBody));
}

/**
 * Processes URL search parameters and validates them using a Zod schema, if provided.
 * @param searchParams - The search parameters object to process.
 * @param validation - The Zod schema to validate the search parameters against.
 * @param withCaseConverter - Whether to convert all search parameter keys to snake_case.
 * @returns The processed search parameters.
 */
export function processSearchParams(
  searchParams: SearchParamsOption,
  validation?: ZodSchema,
  withCaseConverter = true,
): Record<string, string> | undefined {
  if (!searchParams) {
    return undefined;
  }

  // Convert to plain object if needed
  let processedSearchParams =
    searchParams instanceof URLSearchParams
      ? [...searchParams.keys()].reduce<Record<string, string | string[]>>(
          (accumulator, key) => {
            const values = searchParams.getAll(key);
            accumulator[key] = values.length > 1 ? values : values[0];
            return accumulator;
          },
          {},
        )
      : searchParams;

  if (validation) {
    // Validate and sanitize the search params. Also strip all unknown keys. Throw a ZodError if validation fails.
    processedSearchParams = validation.parse(processedSearchParams) as Record<
      string,
      string
    >;
  }

  if (!withCaseConverter) {
    return processedSearchParams as Record<string, string>;
  }

  return snakecaseKeysForApi(processedSearchParams) as Record<string, string>;
}

/**
 * Constructs the final URL with search parameters.
 * @param url - The URL to build.
 * @param searchParams - The search parameters object to add to the URL.
 * @returns The final URL.
 */
export function buildUrl(
  url: string,
  searchParams?: Record<string, string>,
): string {
  // Remove potential trailing slashes since the VESTIGAS API would otherwise reject the request.
  const cleanUrl = url.replace(trailingSlashRegex, '');

  // If there are no new params to add, return the original clean URL (Might include its own params)
  if (!searchParams) {
    return cleanUrl;
  }

  // There are a few cases where searchParams are passed as an instance of URLSearchParams.
  // If that's the case we need to convert them to a plain object.
  if (searchParams instanceof URLSearchParams) {
    searchParams = Object.fromEntries(searchParams.entries());
  }

  // If we pass searchParams to ky, it strips the existing search params from the URL anyway.
  // So we build a new URL.
  const urlObject = new URL(cleanUrl);
  const baseUrl = urlcat(urlObject.origin, urlObject.pathname);

  // FastAPI expects multiple values in the query params to be repeating params,
  // so we need to pass an arrayFormat to urlcat.
  return urlcat(baseUrl, searchParams, {}, { arrayFormat: 'repeat' });
}

/**
 * Re-authenticates by refreshing the access token and retries the original request.
 *
 * @param request - The original request that needs to be retried.
 * @param baseUrl - The base URL for the request.
 * @returns A promise that resolves to the response of the retried request,
 *   regardless of its status code — error handling is left to the caller's
 *   hook chain.
 * @throws SessionExpiredError if the session is simply over, a generic error if
 *   the refresh itself failed, or whatever the retried request rejects with.
 */
const reauthenticateAndRetryRequest = async (
  request: Request,
  baseUrl: string,
  authService: AuthService,
) => {
  try {
    // Try to refresh the token
    const accessToken = await authService.refreshTokens();

    if (!accessToken) {
      throw authService.wasLastRefreshSessionExpiry()
        ? new SessionExpiredError()
        : new Error('Token refresh failed');
    }

    // Retry the original request with the new token
    const retryHeaders = new Headers(request.headers);
    retryHeaders.set('Authorization', `Bearer ${accessToken}`);
    const retryRequest = new Request(request, { headers: retryHeaders });

    // Resolve with the retried response even when it is non-2xx: this bare ky
    // call has no hooks, so a thrown HTTPError would escape the afterResponse
    // hook chain with an unconverted body and without the beforeError wrapping
    // into VestigasHTTPError. Returning the response lets the calling hook
    // apply the case conversion and the outer instance apply its normal
    // throwHttpErrors + beforeError handling.
    return await ky(retryRequest, {
      prefixUrl: baseUrl,
      retry: 0,
      throwHttpErrors: false,
      timeout: false,
    });
  } catch (refreshError) {
    if (!isSessionExpiredError(refreshError)) {
      console.error('Token refresh error:', refreshError);
    }

    throw refreshError; // Re-throw the error to propagate the 401 error if refresh fails.
  }
};

/**
 * Extracts the path segments of a Vestigas API endpoint from a URL.
 * @param url - The URL to extract the path segments from.
 * @param baseUrl - The base URL of the API (e.g. https://app.dev.vestigas.com/api/v1)
 * @returns The path segments of the URL.
 */
export const getEndpointPathSegments = (url: string, baseUrl: string) => {
  if (
    !url ||
    !baseUrl ||
    typeof url !== 'string' ||
    typeof baseUrl !== 'string'
  ) {
    return [];
  }

  return (
    url
      .replace(baseUrl, '')
      .replace(/^\//, '') // Remove leading slash
      .split('?')[0] // Remove search params
      ?.split('/') ?? []
  );
};

export type ExtraHeaders = Partial<
  Record<(typeof CORS_WHITELISTED_EXTRA_HEADERS)[number], string>
>;

/**
 * Builds the outgoing request headers: applies whitelisted extra headers,
 * strips the multipart content-type so ky can regenerate the boundary, and
 * adds the Authorization and Accept-Language headers when configured.
 */
export function buildRequestHeaders(
  request: Request,
  {
    authService,
    extraHeaders,
    localeProvider,
    useIdToken,
  }: {
    authService: AuthService | null;
    extraHeaders?: ExtraHeaders;
    localeProvider?: () => string;
    useIdToken: boolean;
  },
): Headers {
  const headers = new Headers(request.headers);

  if (extraHeaders) {
    // Filter and apply only valid headers (whitelisted and non-empty)
    const whitelistedHeaders = new Set(CORS_WHITELISTED_EXTRA_HEADERS);

    for (const [headerName, value] of Object.entries(extraHeaders)) {
      // Only process headers that are whitelisted and have valid values
      if (
        whitelistedHeaders.has(
          headerName as (typeof CORS_WHITELISTED_EXTRA_HEADERS)[number],
        )
      ) {
        // Validate that the value is a non-empty string
        const validatedValue = z.string().trim().min(1).safeParse(value);

        if (validatedValue.success) {
          headers.set(headerName, validatedValue.data);
        }
      }
    }
  }

  // There is a issue with formData, the ky request already creates a content-type header, but because we run the beforeRequest hook,
  // when we create a new Request at the end, it creates a new content-type header with a different boundary.
  // So we need to delete the existing content-type header.
  if (headers.get('content-type')?.includes('multipart/form-data')) {
    headers.delete('content-type');
  }

  // Add Authorization header only if authService is provided
  if (authService) {
    const token = useIdToken
      ? authService.getIdToken()
      : authService.getAccessToken();
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Add Accept-Language header if localeProvider is provided
  if (localeProvider) {
    headers.set('Accept-Language', localeProvider());
  }

  return headers;
}

/**
 * Derives the response-validation event's identity from the request itself, so
 * no call site hand-passes a key.
 *
 * key = `"<METHOD> <path>"` with the query stripped and UUID / pure-digit
 * segments collapsed to `{id}`, which is what makes one endpoint one Sentry
 * group across every record. `entityId` is the UUID segments that were
 * collapsed out (the logger hashes it before it reaches Sentry).
 *
 * Accepted property: renaming a BE path produces a new key, and therefore a new
 * Sentry group. That is correct — the wire path IS the endpoint's identity.
 */
export function deriveResponseValidationTarget({
  baseUrl,
  method,
  url,
}: {
  baseUrl: string;
  method: string;
  url: string;
}): Pick<ResponseValidationProps, 'entityId' | 'key' | 'message'> {
  const segments = getEndpointPathSegments(url, baseUrl);
  const entityIds = segments.filter((segment) =>
    startsWithUuidRegex.test(segment),
  );
  const path = segments
    .map((segment) =>
      startsWithUuidRegex.test(segment) || digitsOnlySegmentRegex.test(segment)
        ? ID_PLACEHOLDER
        : segment,
    )
    .join('/');
  const key = `${method.toUpperCase()} ${path}`;

  return {
    entityId: entityIds.length > 0 ? entityIds.join(',') : undefined,
    key,
    message: `${key} response has validation errors`,
  };
}

/**
 * Reports a response that was supposed to be JSON but is not — usually a proxy
 * or error page, which is real drift worth seeing.
 *
 * The issue is DELIBERATELY deterministic: a fixed empty path, a stable code,
 * and a digest built from the bare media type. The full content-type (charset
 * and all) lives in the message only, so a varying `charset` parameter cannot
 * change the digest and defeat the logger's 24h dedup.
 */
function reportNonJsonResponse({
  contentType,
  logger,
  target,
}: {
  contentType: string;
  logger: Logger;
  target: Pick<ResponseValidationProps, 'entityId' | 'key' | 'message'>;
}): void {
  const mediaType = contentType.split(';')[0]?.trim().toLowerCase() ?? '';

  // Same promise as `applyResponseSchema`, and it has to be made twice: this is
  // the other `responseValidation` call in the hook. The reporter is handed out
  // unwrapped (`loggerFactory.ts`) and guards only its localStorage access, so
  // a throw here escapes into the request and rejects it — turning a proxy
  // error page into a failed call.
  try {
    logger.responseValidation?.({
      ...target,
      digest: fnv1a(`${INVALID_CONTENT_TYPE_CODE}:${mediaType}`),
      issues: [
        {
          code: INVALID_CONTENT_TYPE_CODE,
          message: `Expected a JSON response, got ${contentType || 'no content-type'}`,
          path: [],
        },
      ],
      totalIssues: 1,
    });
  } catch {
    // Ignore — a reporting failure is not a UI failure.
  }
}

/**
 * Rebuilds a response around a new body, carrying status and headers over.
 * `dropContentLength` is for a re-serialized body, whose byte length no longer
 * matches the header that arrived.
 */
function respondWith(
  response: Response,
  body: string | null,
  { dropContentLength = false }: { dropContentLength?: boolean } = {},
): Response {
  const headers = new Headers(response.headers);

  if (dropContentLength) {
    headers.delete('content-length');
  }

  return new Response(body, {
    headers,
    status: response.status,
    statusText: response.statusText,
  });
}

/**
 * Parses the body, camelCasing it when the converter is enabled.
 *
 * A malformed body under a JSON content-type keeps throwing on converter
 * instances, exactly as before. Validator-only instances never parsed the body
 * at all until now, so a throw there would be a NEW failure mode — they report
 * the miss instead and let `.json()` at the call site fail as it does today.
 */
function parseResponseText(
  text: string,
  withCaseConverter: boolean,
): { body: unknown; isJson: true } | { isJson: false } {
  if (withCaseConverter) {
    return { body: camelcaseKeysFromApi(JSON.parse(text)), isJson: true };
  }

  try {
    return { body: JSON.parse(text), isJson: true };
  } catch {
    return { isJson: false };
  }
}

/**
 * Validates the body if a schema was declared, returning the parsed data on
 * success and the UNTOUCHED body on failure — drift never breaks the UI.
 */
function applyResponseSchema({
  body,
  isResponseSensitive,
  logger,
  schema,
  target,
}: {
  body: unknown;
  isResponseSensitive?: boolean;
  logger?: Logger;
  schema?: ZodSchema;
  target?: Pick<ResponseValidationProps, 'entityId' | 'key' | 'message'>;
}): unknown {
  if (!schema) {
    return body;
  }

  // Validation must never throw. This runs inside the `afterResponse` hook, so
  // anything escaping here escapes into the request itself and turns drift into
  // a failed call — the one outcome the whole design rules out. zod builds
  // `error.message` eagerly via `JSON.stringify`, which a getter or a circular
  // value would throw on before the sanitizer ever sees the issues. Not
  // reachable while the body comes from `JSON.parse` (a plain tree, no getters,
  // no cycles), but keeping the promise costs less than re-auditing it.
  try {
    const validated = schema.safeParse(body, {
      reportInput: !isResponseSensitive,
    });

    if (validated.success) {
      return validated.data;
    }

    if (logger && target) {
      logger.responseValidation?.({
        ...target,
        ...buildResponseValidationReport(validated.error.issues),
      });
    }
  } catch {
    // Fall through to the raw body — a reporting failure is not a UI failure.
  }

  return body;
}

export type ProcessResponseBodyContext = {
  baseUrl?: string;
  logger?: Logger;
  request?: Request;
  validate?: KyValidationOptions;
  withCaseConverter?: boolean;
};

/**
 * The single tap on the response body: converts it to camelCase when the
 * converter is enabled, and validates it when the request declared
 * `validate.response`. Both share one parse — validation is free on converter
 * instances, and instances without the converter parse only when a validator
 * is actually declared.
 *
 * Validation applies to 2xx responses only. Error bodies still get whatever
 * conversion the instance does — ky wraps this hook's response in its
 * HTTPError, so `parsedBody` consumers depend on it — but are never validated,
 * since they carry `{detail}` rather than the success shape. With validation
 * gated there is nothing left for the tap to do on a `withCaseConverter: false`
 * instance, so an error body there is returned exactly as it arrived. That says
 * nothing about a success body, which a passing validator rebuilds from
 * `JSON.stringify` — see `KyValidationOptions.response`.
 *
 * Non-JSON, empty (204 / content-length 0) and empty-text responses are
 * returned unchanged, exactly as before; on a 2xx, a declared validator
 * additionally logs one event for the non-JSON case. Validation NEVER throws:
 * drift is logged and the raw body passes through.
 */
export async function processResponseBody(
  response: Response,
  {
    baseUrl = '',
    logger,
    request,
    validate,
    withCaseConverter = false,
  }: ProcessResponseBodyContext = {},
): Promise<Response> {
  const { isResponseSensitive, response: responseSchema } = validate ?? {};

  // Error responses carry `{detail}`, never the success shape, so validating
  // them is guaranteed-failing noise — and it consumes the logger's
  // per-endpoint daily cap that real drift needs. ky runs afterResponse BEFORE
  // its own `!ok` throw, so this is the only place that can gate it.
  //
  // Only VALIDATION is gated. Case conversion still runs: the response this
  // hook returns is the one ky wraps in HTTPError, so skipping it here would
  // revert every multi-word key in `VestigasHTTPError.parsedBody` to
  // snake_case.
  const schema = response.ok ? responseSchema : undefined;

  // Nothing to convert and nothing to validate — leave the response alone
  // rather than paying for a parse.
  if (!withCaseConverter && !schema) {
    return response;
  }

  const target =
    schema && request
      ? deriveResponseValidationTarget({
          baseUrl,
          method: request.method,
          url: request.url,
        })
      : undefined;

  // 204 No Content or empty body: skip JSON parsing. Ordered ABOVE the
  // content-type check because a 204 usually arrives with no content-type at
  // all, which would otherwise be reported as a missing JSON response.
  if (
    response.status === 204 ||
    response.headers.get('content-length') === '0'
  ) {
    return response;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (logger && target) {
      reportNonJsonResponse({ contentType, logger, target });
    }

    return response;
  }

  // Some servers may not set content-length, so also check if body is empty.
  // `response.text()` consumes the body, so we must return a fresh Response
  // rather than the now-consumed original.
  const text = await response.text();
  if (!text) {
    return respondWith(response, null);
  }

  const parsed = parseResponseText(text, withCaseConverter);
  if (!parsed.isJson) {
    return respondWith(response, text);
  }

  const outputBody = applyResponseSchema({
    body: parsed.body,
    isResponseSensitive,
    logger,
    schema,
    target,
  });

  // Validation failed (or there was none) on an instance that does not convert:
  // the body is byte-identical to what arrived, so hand back the original text
  // and keep its content-length.
  return outputBody === parsed.body && !withCaseConverter
    ? respondWith(response, text)
    : respondWith(response, JSON.stringify(outputBody), {
        dropContentLength: true,
      });
}

/**
 * Logs a Zod validation error raised while preparing a request. The raw Zod
 * details are logged but stripped from the error thrown to the caller.
 */
function logZodValidationError(
  error: ZodError,
  request: Request,
  options: KyHookOptions,
  logger?: Logger,
): void {
  // Parse the body for logging defensively: the ZodError may originate from
  // searchParams/pathSegments validation while options.body is a non-JSON
  // string, so a throwing JSON.parse would mask the original validation error.
  let loggedBody: unknown = options.body;
  if (options.body && typeof options.body === 'string') {
    try {
      loggedBody = JSON.parse(options.body);
    } catch {
      loggedBody = options.body;
    }
  }

  logger?.error?.({
    message: 'ZOD_VALIDATION_ERROR:',
    error: {
      context: {
        hasBody: Boolean(options.body),
        hasBodyValidation: Boolean(options.validate?.body),
        hasPathSegmentValidation: Boolean(options.validate?.pathSegments),
        hasSearchParams: Boolean(options.searchParams),
        hasSearchParamsValidation: Boolean(options.validate?.searchParams),
        pathSegments: options.validate?.pathSegments?._def?.items?.map(
          (item: any) => item.value,
        ),
        url: request.url,
        values: {
          body: loggedBody,
          searchParams: options.searchParams,
        },
      },
      errors: error.issues,
      message: 'Request validation failed',
      name: 'ZodValidationError',
      stack: error.stack,
    },
  });
}

/**
 * Creates the `afterResponse` hook: refreshes the token and retries on 401
 * (when auth is enabled), and converts the response body to camelCase when the
 * case converter is enabled.
 */
export function createAfterResponseHook({
  authService,
  baseUrl,
  logger,
  withCaseConverter,
}: {
  authService: AuthService | null;
  baseUrl: string;
  logger?: Logger;
  withCaseConverter: boolean;
}) {
  return async (
    request: Request,
    options: KyHookOptions,
    response: Response,
  ) => {
    const context: ProcessResponseBodyContext = {
      baseUrl,
      logger,
      request,
      validate: options.validate,
      withCaseConverter,
    };

    // Handle 401 by refreshing token and retrying (only if auth is enabled).
    // The retry bypasses the ky hooks, so the retried response must go through
    // the same tap here — case conversion AND response validation.
    if (response.status === 401 && authService) {
      const retriedResponse = await reauthenticateAndRetryRequest(
        request,
        baseUrl,
        authService,
      );

      return processResponseBody(retriedResponse, context);
    }

    return processResponseBody(response, context);
  };
}

/**
 * Creates the `beforeRequest` hook: builds headers, processes and validates the
 * body, search params, and path segments, then rebuilds the request with the
 * final URL. Zod validation errors are logged and rethrown as a generic error.
 */
export function createBeforeRequestHook({
  authService,
  baseUrl,
  extraHeaders,
  localeProvider,
  logger,
  useIdToken,
  withCaseConverter,
}: {
  authService: AuthService | null;
  baseUrl: string;
  extraHeaders?: ExtraHeaders;
  localeProvider?: () => string;
  logger?: Logger;
  useIdToken: boolean;
  withCaseConverter: boolean;
}) {
  return async (request: Request, options: KyHookOptions) => {
    try {
      const headers = buildRequestHeaders(request, {
        authService,
        extraHeaders,
        localeProvider,
        useIdToken,
      });

      const processedBody = processRequestBody(
        options.body ?? undefined,
        options.validate?.body,
        withCaseConverter,
      );

      const processedSearchParams = processSearchParams(
        options.searchParams,
        options.validate?.searchParams,
      );

      if (options.validate?.pathSegments) {
        const pathSegments = getEndpointPathSegments(request.url, baseUrl);

        // Validate the path segments of the request URL.
        options.validate.pathSegments.parse(pathSegments);
      }

      const finalUrl = buildUrl(request.url, processedSearchParams);

      // Create a new request with the modified URL, headers, and body.
      return new Request(finalUrl, {
        ...options,
        body: processedBody,
        headers,
      });
    } catch (error) {
      if (error instanceof ZodError) {
        // This is a Zod validation error
        logZodValidationError(error, request, options, logger);

        // Strip Zod error details and throw a simple validation error.
        throw new Error('Request validation failed');
      }

      throw error;
    }
  };
}

/**
 * Creates a configured ky instance for making HTTP requests to the VESTIGAS API.
 *
 * The instance is configured with the following features:
 * - **Authentication**: Adds Bearer token to requests (when `authService` is provided)
 * - **Token refresh**: Automatically refreshes expired tokens on 401 responses
 * - **Case conversion**: Converts request bodies to snake_case and responses to camelCase
 * - **URL handling**: Removes trailing slashes, processes search parameters
 * - **Validation**: Accepts Zod schemas for request body, search parameter and response
 *   validation. Response schemas apply to 2xx responses only. A failing response is
 *   logged via `logger.responseValidation` and passed through untouched — response
 *   validation never breaks the UI.
 * - **Error handling**: Wraps errors in VestigasHTTPError for consistent error handling
 * - **Timeouts/retries**: Disabled by default (managed by TanStack Query)
 *
 * @param options - Configuration options for the ky instance.
 * @param options.authService - Authentication service for managing tokens. Pass `null` for
 *   public endpoints that don't require authentication. When `null`, no Authorization header
 *   is added and 401 responses are not retried with refreshed tokens.
 * @param options.baseUrl - The base URL for all requests (e.g., 'https://api.vestigas.com/v1').
 * @param options.extraHeaders - Additional headers to include in requests. Only whitelisted
 *   headers are allowed (e.g., 'Customer-Portal-Version-Git').
 * @param options.localeProvider - Function that returns the current user locale (e.g., 'de', 'en').
 *   When provided, sets the `Accept-Language` header on all requests for backend localization.
 * @param options.logger - Logger instance for error reporting. Used to log request validation
 *   errors, and response drift via `responseValidation` when a request declares
 *   `validate.response`.
 * @param options.useIdToken - When `true`, uses the ID token instead of the access token
 *   for authentication. Defaults to `false`. Only applicable when `authService` is provided.
 * @param options.withCaseConverter - When `true`, converts request body keys to snake_case
 *   and response body keys to camelCase. Defaults to `false`.
 * @returns A configured ky instance with custom hooks for authentication, validation, and
 *   error handling.
 *
 * @example
 * // Authenticated API instance
 * const api = createKyInstance({
 *   authService: AuthService,
 *   baseUrl: 'https://api.vestigas.com/v1',
 *   logger: log,
 *   withCaseConverter: true,
 * });
 *
 * @example
 * // Public API instance (no authentication)
 * const publicApi = createKyInstance({
 *   authService: null,
 *   baseUrl: 'https://api.vestigas.com/v1',
 *   withCaseConverter: true,
 * });
 */
export function createKyInstance({
  authService,
  baseUrl,
  extraHeaders,
  localeProvider,
  logger,
  useIdToken = false,
  withCaseConverter = false,
}: {
  authService: AuthService | null;
  baseUrl: string;
  extraHeaders?: ExtraHeaders;
  localeProvider?: () => string;
  logger?: Logger;
  useIdToken?: boolean;
  withCaseConverter?: boolean;
}): VestigasKyInstance {
  const kyInstance = ky.create({
    hooks: {
      afterResponse: [
        createAfterResponseHook({
          authService,
          baseUrl,
          logger,
          withCaseConverter,
        }),
      ],
      beforeRequest: [
        createBeforeRequestHook({
          authService,
          baseUrl,
          extraHeaders,
          localeProvider,
          logger,
          useIdToken,
          withCaseConverter,
        }),
      ],
      beforeError: [async (error) => VestigasHTTPError.create(error)],
    },
    prefixUrl: baseUrl,
    retry: 0, // Disable retries (managed by react-query)
    timeout: false, // Disable timeouts (managed by react-query)
  });

  return kyInstance as VestigasKyInstance;
}
